<template>
    <div class="flex flex-col gap-4 pb-10 pt-1">
        <div class="flex flex-wrap items-end justify-between gap-4">
            <div class="flex flex-col gap-1">
                <h1
                    class="m-0 text-[22px] font-semibold tracking-tight text-zinc-950 dark:text-white"
                >
                    Cockpit de saques
                </h1>
                <p class="m-0 text-[13.5px] text-zinc-500 dark:text-gray-400">
                    Double check dos saques solicitados. Decida, envie, siga
                    para o próximo.
                </p>
            </div>
            <button
                type="button"
                class="flex h-[34px] items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-[13px] font-medium text-zinc-950 hover:bg-zinc-100 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700"
                :disabled="loading"
                @click="refreshAll"
            >
                <ArrowPathIcon
                    class="h-3.5 w-3.5"
                    :class="loading ? 'animate-spin' : ''"
                />
                Atualizar
            </button>
        </div>

        <operator-stats
            ref="operatorStats"
            :urgent-count="urgentCashoutCount"
            :high-risk-count="highRiskCount"
            :remaining-time="remainingTime"
            :remaining-time-formatted="remainingTimeFormatted"
            :mid-day-passed="midDayPassed"
            :urgent-active="isFilteringUrgent"
            @toggle-urgent="filterUrgentReviews"
        />

        <div
            class="grid items-start gap-4 lg:grid-cols-[minmax(240px,280px)_minmax(0,1fr)] xl:grid-cols-[minmax(260px,300px)_minmax(0,1fr)_minmax(300px,340px)]"
        >
            <review-queue-panel
                class="lg:sticky lg:top-4 lg:row-span-2 xl:row-span-1"
                :reviews="visibleReviews"
                :selected-id="selectedReviewId"
                :loading="loading"
                :total="queueTotal"
                :has-more="hasMore"
                :skipped-ids="skippedReviewIds"
                :is-same-day-urgent="isSameDayUrgent"
                :statuses="statuses"
                :min-approved="minApproved"
                v-model:query="query"
                v-model:sort="sort"
                @select="selectReview"
                @load-more="loadMore"
                @apply-filters="applyFilters"
            />

            <template v-if="selectedReview">
                <review-decision-panel
                    ref="reviewDecisionPanel"
                    :review="selectedReview"
                    :payable-details="selectedPayableDetails"
                    :is-same-day-urgent="isSameDayUrgent(selectedReview)"
                    :is-skipped="skippedReviewIds.includes(selectedReview.id)"
                    @checklist-loaded="onChecklistLoaded"
                />

                <review-decision-form
                    ref="decisionForm"
                    class="xl:sticky xl:top-4"
                    :review="selectedReview"
                    :analysis="selectedPayableDetails?.audit?.analysis"
                    :checklist="checklistByCashout[selectedReview.cashout.id]"
                    @submit="decide"
                    @skip="skipSelected"
                />
            </template>

            <section
                v-else-if="!loading"
                class="flex flex-col items-center gap-2 rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-12 text-center lg:col-span-1 xl:col-span-2 dark:border-gray-600 dark:bg-gray-800"
            >
                <span
                    class="text-base font-semibold text-zinc-950 dark:text-white"
                >
                    {{ queueEmptyTitle }}
                </span>
                <span class="text-[13.5px] text-zinc-500 dark:text-gray-400">
                    {{ queueEmptyText }}
                </span>
                <button
                    v-if="skippedReviewIds.length"
                    type="button"
                    class="mt-2 h-8 rounded-lg border border-zinc-200 px-3 text-[13px] font-medium text-zinc-900 hover:bg-zinc-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                    @click="clearSkipped"
                >
                    Rever {{ skippedReviewIds.length }} pulados
                </button>
            </section>
        </div>

        <div
            v-if="toast"
            class="fixed bottom-5 left-1/2 z-[60] flex -translate-x-1/2 items-center gap-3.5 rounded-[10px] bg-zinc-900 py-2.5 pl-3.5 pr-3 text-[13.5px] text-zinc-50 shadow-[0_10px_30px_rgba(0,0,0,0.18)] dark:bg-gray-950"
        >
            <span class="h-2 w-2 rounded-full" :class="toast.dotClass"></span>
            <span>{{ toast.text }}</span>
            <button
                v-if="toast.undoable"
                type="button"
                class="h-7 rounded-md border border-zinc-700 px-2.5 text-[12.5px] font-medium text-zinc-50 hover:bg-zinc-800"
                @click="undoDecision"
            >
                Desfazer
            </button>
        </div>
    </div>
</template>

<script>
import { ArrowPathIcon } from "@heroicons/vue/24/outline";
import OperatorStats from "../../components/cashouts/reviews/OperatorStats.vue";
import ReviewQueuePanel from "../../components/cashouts/reviews/ReviewQueuePanel.vue";
import ReviewDecisionPanel from "../../components/cashouts/reviews/ReviewDecisionPanel.vue";
import ReviewDecisionForm from "../../components/cashouts/reviews/ReviewDecisionForm.vue";
import { parseBrazilianDate } from "../../utils/dateHelpers.js";
import { useToast } from "vue-toastification";
import axios from "axios";

/** Janela para desfazer: a decisão só vai ao servidor depois dela. */
const UNDO_WINDOW_MS = 5000;

const DECISION_FEEDBACK = {
    approved: { past: "aprovado", dotClass: "bg-green-500" },
    warning: { past: "marcado com atenção", dotClass: "bg-amber-500" },
    rejected: { past: "reprovado", dotClass: "bg-red-500" },
};

export default {
    name: "ReviewsCockpit",
    components: {
        ArrowPathIcon,
        OperatorStats,
        ReviewQueuePanel,
        ReviewDecisionPanel,
        ReviewDecisionForm,
    },
    setup() {
        const toast = useToast();
        return { notify: toast };
    },
    data() {
        return {
            loading: false,
            urgentCashoutCount: 0,
            reviews: [],
            selectedReviewId: null,
            // Vindo da fila de decisões (?review=ID): consumido na primeira
            // carga; se o saque não estiver na página atual, cai no padrão.
            deepLinkReviewId: null,
            skippedReviewIds: JSON.parse(
                localStorage.getItem("cockpit-skipped-reviews") || "[]",
            ),
            statuses: ["pending", "approved"],
            minApproved: "",
            isFilteringUrgent: false,
            query: "",
            sort: "prazo",
            remainingTime: 0,
            midDayPassed: false,
            timerInterval: null,
            pagination: {
                meta: {
                    current_page: 1,
                    last_page: 1,
                    per_page: 50,
                    total: 0,
                },
            },
            payableDetails: {},
            checklistByCashout: {},
            pendingDecision: null,
            toast: null,
            toastTimer: null,
        };
    },
    computed: {
        remainingTimeFormatted() {
            if (this.midDayPassed) {
                return "00:00:00";
            }

            const hours = Math.floor(this.remainingTime / 3600);
            const minutes = Math.floor((this.remainingTime % 3600) / 60);
            const seconds = this.remainingTime % 60;

            return [hours, minutes, seconds]
                .map((part) => part.toString().padStart(2, "0"))
                .join(":");
        },
        hasMore() {
            return (
                this.pagination.meta.current_page <
                this.pagination.meta.last_page
            );
        },
        queueTotal() {
            return Math.max(
                0,
                this.pagination.meta.total - (this.pendingDecision ? 1 : 0),
            );
        },
        highRiskCount() {
            return this.visibleReviews.filter((review) => {
                const score = review?.seller?.score;
                return score !== undefined && score !== null && score <= 25;
            }).length;
        },
        visibleReviews() {
            const term = this.query.trim().toLowerCase();
            const pendingId = this.pendingDecision?.review.id;

            const list = this.reviews.filter((review) => {
                if (review.id === pendingId) {
                    return false;
                }

                if (!term) {
                    return true;
                }

                const user = review.cashout?.user || {};

                return [
                    user.name,
                    user.email,
                    String(review.cashout?.id ?? ""),
                    String(review.id),
                ].some((value) => (value || "").toLowerCase().includes(term));
            });

            return list.sort((a, b) => {
                const skippedDiff =
                    (this.skippedReviewIds.includes(a.id) ? 1 : 0) -
                    (this.skippedReviewIds.includes(b.id) ? 1 : 0);

                if (skippedDiff !== 0) {
                    return skippedDiff;
                }

                if (this.sort === "valor") {
                    return (
                        Number(b.cashout?.amount || 0) -
                        Number(a.cashout?.amount || 0)
                    );
                }

                if (this.sort === "risco") {
                    return this.scoreOf(a) - this.scoreOf(b);
                }

                const urgentDiff =
                    (this.isSameDayUrgent(a) ? 0 : 1) -
                    (this.isSameDayUrgent(b) ? 0 : 1);

                if (urgentDiff !== 0) {
                    return urgentDiff;
                }

                const scoreDiff = this.scoreOf(a) - this.scoreOf(b);

                if (scoreDiff !== 0) {
                    return scoreDiff;
                }

                return this.createdAtOf(a) - this.createdAtOf(b);
            });
        },
        selectedReview() {
            if (!this.selectedReviewId) {
                return null;
            }

            return (
                this.visibleReviews.find(
                    (review) => review.id === this.selectedReviewId,
                ) || null
            );
        },
        selectedPayableDetails() {
            if (!this.selectedReview) {
                return null;
            }

            return this.payableDetails[this.selectedReview.cashout.id] || null;
        },
        queueEmptyTitle() {
            if (this.query.trim()) {
                return "Nenhum saque encontrado";
            }

            return this.isFilteringUrgent
                ? "Fila zerada"
                : "Nenhum saque na fila";
        },
        queueEmptyText() {
            if (this.query.trim()) {
                return "Ajuste a busca para ver outros saques.";
            }

            return this.isFilteringUrgent
                ? "Todos os saques até 12h foram decididos."
                : "Não há saques para os filtros atuais.";
        },
    },
    watch: {
        visibleReviews() {
            this.ensureSelection();
        },
    },
    mounted() {
        this.deepLinkReviewId = Number(this.$route.query.review) || null;
        this.startMidDayTimer();
        this.fetch();
        this.fetchUrgentCount();
        window.addEventListener("keydown", this.handleKeydown);
        window.addEventListener("beforeunload", this.handleBeforeUnload);
    },
    beforeUnmount() {
        clearInterval(this.timerInterval);
        clearTimeout(this.toastTimer);
        window.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
        this.commitPendingDecision();
    },
    methods: {
        scoreOf(review) {
            return review?.seller?.score ?? 100;
        },
        createdAtOf(review) {
            const date = parseBrazilianDate(review?.cashout?.created_at);
            return date ? date.getTime() : 0;
        },
        startMidDayTimer() {
            let lastCountUpdate = 0;

            const updateTimer = () => {
                const now = new Date();
                const midDay = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    12,
                    0,
                    0,
                );

                if (now >= midDay) {
                    this.midDayPassed = true;
                    this.remainingTime = 0;
                } else {
                    this.midDayPassed = false;
                    this.remainingTime = Math.floor((midDay - now) / 1000);
                }

                const currentTime = Math.floor(Date.now() / 1000);
                if (
                    (this.midDayPassed || this.remainingTime < 1800) &&
                    currentTime - lastCountUpdate > 60
                ) {
                    this.fetchUrgentCount();
                    lastCountUpdate = currentTime;
                }
            };

            updateTimer();
            this.timerInterval = setInterval(updateTimer, 1000);
        },
        requestParams(page) {
            const params = {
                page,
                per_page: this.pagination.meta.per_page,
                "filter[status]": this.statuses.join(),
                "filter[minApproved]": this.minApproved,
            };

            if (this.isFilteringUrgent) {
                params["filter[urgentMidDay]"] = true;
            }

            return params;
        },
        async fetch({ append = false } = {}) {
            const page = append ? this.pagination.meta.current_page + 1 : 1;
            this.loading = true;

            try {
                const { data } = await axios.get("/api/cashout/review", {
                    params: this.requestParams(page),
                });

                const incoming = Object.values(data.data || {});

                if (append) {
                    const knownIds = new Set(this.reviews.map((r) => r.id));
                    this.reviews = [
                        ...this.reviews,
                        ...incoming.filter(
                            (review) => !knownIds.has(review.id),
                        ),
                    ];
                } else {
                    this.reviews = incoming;
                }

                this.pagination = { ...data, data: undefined };
                this.ensureSelection();
            } catch (error) {
                console.error(
                    "[ReviewsCockpit] Erro ao buscar reviews:",
                    error,
                );
                this.notify.error(
                    "Não foi possível carregar a fila de saques.",
                );
            } finally {
                this.loading = false;
            }
        },
        loadMore() {
            if (this.hasMore && !this.loading) {
                this.fetch({ append: true });
            }
        },
        refreshAll() {
            this.fetch();
            this.fetchUrgentCount();
            this.$refs.operatorStats?.fetchStats();
            this.$refs.reviewDecisionPanel?.reloadCashoutChecklist();
        },
        applyFilters({ statuses, minApproved }) {
            this.statuses = statuses;
            this.minApproved = minApproved;
            this.fetch();
        },
        fetchUrgentCount() {
            axios
                .get("/api/cashout/review/count", {
                    params: { "filter[status]": "pending" },
                })
                .then(({ data }) => {
                    this.urgentCashoutCount = data.count || 0;
                })
                .catch((error) => {
                    console.error(
                        "Erro ao buscar contagem de saques urgentes:",
                        error,
                    );
                });
        },
        filterUrgentReviews() {
            this.isFilteringUrgent = !this.isFilteringUrgent;
            this.fetch();
            this.fetchUrgentCount();
        },
        isSameDayUrgent(review) {
            // Prazo do servidor (CashoutDeadline, mesma régua da fila de
            // decisões): urgente quando vence até o fim de hoje.
            if (!review?.due_at) {
                return false;
            }

            const due = new Date(review.due_at.replace(" ", "T"));
            const endOfToday = new Date();
            endOfToday.setHours(23, 59, 59, 999);

            return due <= endOfToday;
        },
        loadPayableDetails(review) {
            const cashoutId = review?.cashout?.id;

            if (!cashoutId || this.payableDetails[cashoutId]) {
                return;
            }

            axios
                .get(`/api/cashout/${cashoutId}/payables/details`)
                .then(({ data }) => {
                    this.payableDetails = {
                        ...this.payableDetails,
                        [cashoutId]: data,
                    };
                })
                .catch((error) => {
                    console.error(
                        `[ReviewsCockpit] Erro ao buscar detalhes do cashout ${cashoutId}:`,
                        error,
                    );
                });
        },
        onChecklistLoaded(data) {
            if (!data || !this.selectedReview) {
                return;
            }

            this.checklistByCashout = {
                ...this.checklistByCashout,
                [this.selectedReview.cashout.id]: data,
            };
        },
        ensureSelection() {
            const list = this.visibleReviews;

            if (this.deepLinkReviewId && this.reviews.length) {
                const deepLinkId = this.deepLinkReviewId;
                const target = list.find((review) => review.id === deepLinkId);

                this.deepLinkReviewId = null;
                this.clearReviewQuery();

                if (target) {
                    this.selectReview(target);
                    return;
                }

                // Fora da página atual da fila: avisa em vez de trocar de
                // saque silenciosamente com o ?review= ainda na URL.
                this.notify.warning(
                    `Saque da revisão #${deepLinkId} não está na página atual da fila.`,
                );
            }

            if (list.some((review) => review.id === this.selectedReviewId)) {
                return;
            }

            if (list[0]) {
                this.selectReview(list[0]);
            } else {
                this.selectedReviewId = null;
            }
        },
        clearReviewQuery() {
            if (!this.$route.query.review) {
                return;
            }

            const { review, ...query } = this.$route.query;
            this.$router.replace({ query });
        },
        selectReview(review) {
            if (!review) {
                return;
            }

            this.selectedReviewId = review.id;
            this.loadPayableDetails(review);
        },
        neighbourOf(reviewId, direction = 1) {
            const list = this.visibleReviews;
            const index = list.findIndex((review) => review.id === reviewId);

            if (index === -1) {
                return list[0] || null;
            }

            return list[index + direction] || null;
        },
        moveSelection(direction) {
            const next = this.neighbourOf(this.selectedReviewId, direction);

            if (next) {
                this.selectReview(next);
            }
        },
        skipSelected() {
            if (!this.selectedReview) {
                return;
            }

            const skippedId = this.selectedReview.id;
            const next =
                this.neighbourOf(skippedId, 1) ||
                this.visibleReviews.find((review) => review.id !== skippedId);

            if (!this.skippedReviewIds.includes(skippedId)) {
                this.skippedReviewIds = [...this.skippedReviewIds, skippedId];
                this.persistSkipped();
            }

            if (next) {
                this.selectReview(next);
            }
        },
        clearSkipped() {
            this.skippedReviewIds = [];
            this.persistSkipped();
            this.ensureSelection();
        },
        forgetSkipped(reviewId) {
            if (this.skippedReviewIds.includes(reviewId)) {
                this.skippedReviewIds = this.skippedReviewIds.filter(
                    (id) => id !== reviewId,
                );
                this.persistSkipped();
            }
        },
        persistSkipped() {
            localStorage.setItem(
                "cockpit-skipped-reviews",
                JSON.stringify(this.skippedReviewIds),
            );
        },
        decide({ status, content }) {
            const review = this.selectedReview;

            if (!review) {
                return;
            }

            this.commitPendingDecision();

            const next =
                this.neighbourOf(review.id, 1) ||
                this.neighbourOf(review.id, -1);

            this.pendingDecision = {
                review,
                payload: { ...review, status, content },
                timer: setTimeout(
                    () => this.commitPendingDecision(),
                    UNDO_WINDOW_MS,
                ),
            };

            this.showToast({
                text: `Saque #${review.cashout.id} ${DECISION_FEEDBACK[status].past}`,
                dotClass: DECISION_FEEDBACK[status].dotClass,
                undoable: true,
            });

            if (next) {
                this.selectReview(next);
            } else {
                this.selectedReviewId = null;
            }
        },
        undoDecision() {
            const pending = this.pendingDecision;

            if (!pending) {
                return;
            }

            clearTimeout(pending.timer);
            this.pendingDecision = null;
            this.hideToast();
            this.selectReview(pending.review);
        },
        commitPendingDecision() {
            const pending = this.pendingDecision;

            if (!pending) {
                return Promise.resolve();
            }

            clearTimeout(pending.timer);
            this.pendingDecision = null;

            if (this.toast?.undoable) {
                this.hideToast();
            }

            const { review, payload } = pending;

            return axios
                .put(
                    `/api/cashout/${review.cashout.id}/review/${review.id}`,
                    payload,
                )
                .then(() => {
                    this.onDecisionCommitted(review, payload.status);
                })
                .catch((error) => {
                    console.error(
                        `[ReviewsCockpit] Erro ao registrar decisão do saque ${review.cashout.id}:`,
                        error,
                    );
                    this.notify.error(
                        `Não foi possível registrar a decisão do saque #${review.cashout.id}. Ele voltou para a fila.`,
                    );
                });
        },
        onDecisionCommitted(review, status) {
            this.forgetSkipped(review.id);

            if (this.statuses.includes(status)) {
                this.reviews = this.reviews.map((item) =>
                    item.id === review.id ? { ...item, status } : item,
                );
            } else {
                this.reviews = this.reviews.filter(
                    (item) => item.id !== review.id,
                );
                this.pagination.meta.total = Math.max(
                    0,
                    this.pagination.meta.total - 1,
                );
            }

            if (review.status === "pending" && this.isSameDayUrgent(review)) {
                this.urgentCashoutCount = Math.max(
                    0,
                    this.urgentCashoutCount - 1,
                );
            }

            this.$refs.operatorStats?.fetchStats();
            this.fetchUrgentCount();
        },
        showToast(toast) {
            clearTimeout(this.toastTimer);
            this.toast = toast;
            this.toastTimer = setTimeout(
                () => this.hideToast(),
                UNDO_WINDOW_MS,
            );
        },
        hideToast() {
            clearTimeout(this.toastTimer);
            this.toast = null;
        },
        handleBeforeUnload(event) {
            if (!this.pendingDecision) {
                return;
            }

            this.commitPendingDecision();
            event.preventDefault();
            event.returnValue = "";
        },
        handleKeydown(event) {
            if (event.defaultPrevented) {
                return;
            }

            if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                if (this.$refs.decisionForm) {
                    event.preventDefault();
                    this.$refs.decisionForm.submit();
                }
                return;
            }

            const target = event.target;
            const tag = (target?.tagName || "").toLowerCase();

            if (
                event.metaKey ||
                event.ctrlKey ||
                event.altKey ||
                ["input", "textarea", "select"].includes(tag) ||
                target?.isContentEditable
            ) {
                return;
            }

            const key = event.key.toLowerCase();
            const decisionKeys = { a: "approved", t: "warning", r: "rejected" };

            if (key === "j" || key === "k") {
                event.preventDefault();
                this.moveSelection(key === "j" ? 1 : -1);
            } else if (decisionKeys[key] && this.$refs.decisionForm) {
                event.preventDefault();
                this.$refs.decisionForm.pick(decisionKeys[key]);
            } else if (key === "s" && this.selectedReview) {
                event.preventDefault();
                this.skipSelected();
            }
        },
    },
};
</script>
